<template>
  <q-dialog v-model="isOpen" maximized transition-show="fade" transition-hide="fade" @hide="$emit('close')">
    <div class="container">
      <div class="top-bar">
        <span class="filename">{{ currentImage.name || currentImage.filename }}</span>
        <q-btn no-caps flat class="action-btn" color="grey-6" padding="10px">
          <template v-slot:default>
            <q-icon name="o_download" class="q-mr-sm" color="grey-7" size="sm" />
            Download
          </template>
        </q-btn>
        <q-btn no-caps flat class="action-btn" color="grey-6" padding="10px">
          <template v-slot:default>
            <q-icon name="o_edit" class="q-mr-sm" color="grey-7" size="sm" />
            Edit with AI
          </template>
        </q-btn>
        <q-btn no-caps class="action-btn" color="primary" text-color="grey-7" padding="10px">
          <template v-slot:default>
            <q-icon name="o_share" class="q-mr-sm" color="grey-7" size="sm" />
            Publish
          </template>
        </q-btn>
      </div>
      <div class="lightbox-container">
        <div class="lightbox-content">
          <div class="image-container">
            <img :src="currentImage.url" alt="Image preview" style="max-width: 100%; max-height: 100%; width: auto; height: auto" fit="contain" />
          </div>
        </div>

        <!-- Right sidebar -->
        <div class="sidebar" style="display: flex; flex-direction: row; height: 100%">
          <q-tab-panels v-model="sidebarTab" animated vertical style="flex: 1; height: 100%">
            <q-tab-panel name="info" class="q-pa-none" style="height: 100%">
              <q-scroll-area style="height: 100%">
                <div class="sidebar-content">
                  <!-- Info section -->
                  <div class="sidebar-section">
                    <div class="section-header">
                      <q-icon name="o_info" size="sm" />
                      <span class="section-title">File details</span>
                      <q-icon name="keyboard_arrow_down" class="expand-icon" />
                    </div>

                    <div class="details-list">
                      <div class="detail-item">
                        <div class="detail-label">Type:</div>
                        <div class="detail-value">{{ getFileExtension(currentImage.filename) }}</div>
                      </div>
                      <div class="detail-item">
                        <div class="detail-label">Size:</div>
                        <div class="detail-value">{{ imageSizeMB }}</div>
                      </div>
                      <div class="detail-item">
                        <div class="detail-label">Resolution:</div>
                        <div class="detail-value">{{ currentImage.width }} x {{ currentImage.height }}</div>
                      </div>
                      <div class="detail-item">
                        <div class="detail-label">Uploader:</div>
                        <div class="detail-value">{{ currentImage.uploaded_by }}</div>
                      </div>
                      <div class="detail-item">
                        <div class="detail-label">Upload date:</div>
                        <div class="detail-value">{{ currentImage.created_at }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Labels section -->
                  <div class="sidebar-section">
                    <div class="section-header">
                      <q-icon name="o_local_offer" size="sm" />
                      <span class="section-title">Tags</span>
                      <q-icon name="keyboard_arrow_down" class="expand-icon" />
                    </div>

                    <div class="tags-container">
                      <q-chip v-for="tag in currentImage.labels" :key="tag" removable dense class="tag-chip" @remove="confirmRemoveLabel(tag)">
                        {{ tag }}
                      </q-chip>
                      <q-btn no-caps flat dense size="sm" icon="add" label="Add tag" class="add-tag-btn" @click="showAddLabelDialog = true" />
                    </div>
                  </div>
                </div>
              </q-scroll-area>
            </q-tab-panel>
            <q-tab-panel name="comments" class="q-pa-md" style="height: 100%">
              <div class="comments-section">
                <div class="section-header">
                  <q-icon name="o_comment" size="sm" />
                  <span class="section-title">Comments</span>
                  <q-icon name="keyboard_arrow_down" class="expand-icon" />
                </div>
                <q-scroll-area style="height: calc(100% - 90px); padding-bottom: 60px">
                  <!-- Empty state when no comments -->
                  <div v-if="!currentImage.comments || currentImage.comments.length === 0" class="comments-empty-state">
                    <q-icon name="o_comment" size="48px" color="grey-4" />
                    <div class="text-subtitle1 q-mt-md">No comments yet</div>
                    <div class="text-caption text-grey-7">Comments will appear here when added</div>
                  </div>

                  <!-- Comments list -->
                  <div v-else class="comments-list">
                    <div v-for="comment in sortedComments" :key="comment.id" class="comment-item">
                      <div class="comment-layout">
                        <q-avatar size="36px" color="amber-3" text-color="grey-9" class="comment-avatar">
                          {{ comment.user_name ? comment.user_name.charAt(0).toUpperCase() : 'U' }}
                        </q-avatar>
                        <div class="comment-content-wrapper">
                          <div class="comment-header">
                            <div class="comment-author">{{ comment.user_name }}</div>
                            <div class="comment-time">
                              {{ formatCommentDate(comment.created_at) }}
                              <span v-if="isCommentEdited(comment)" class="comment-edited"> (edited {{ formatCommentDate(comment.updated_at) }}) </span>
                            </div>
                          </div>
                          <div class="comment-content">{{ comment.content }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </q-scroll-area>

                <!-- Comment input section -->
                <div class="comment-input-section">
                  <div class="comment-input-header">
                    <span class="comment-input-title">Leave a comment</span>
                  </div>
                  <div class="comment-input-container">
                    <q-avatar size="40px" color="amber-3" text-color="grey-8" class="comment-avatar" icon="o_person" />
                    <div class="comment-input-wrapper">
                      <q-input v-model="commentInput" placeholder="Add a comment..." outlined @keyup.enter="handleAddComment" type="textarea" style="width: 100%; border-radius: 12px" />
                      <q-btn class="send-btn" rounded :loading="addingComment" :disable="!commentInput.trim()" @click="handleAddComment" dense no-caps>
                        Send <q-icon name="arrow_forward" size="12px" class="q-ml-xs" />
                      </q-btn>
                    </div>
                  </div>
                </div>
              </div>
            </q-tab-panel>
          </q-tab-panels>
        </div>

        <!-- I want a toolbar to switch between different features like comments & file info -->
        <q-tabs v-model="sidebarTab" vertical dense class="tabs" indicator-color="primary">
          <q-tab name="info" label="Info" icon="o_info" no-caps />
          <q-tab name="comments" label="Comments" icon="o_comment" no-caps />
        </q-tabs>
      </div>
    </div>
    <template>
      <q-dialog v-model="showAddLabelDialog" persistent>
        <q-card style="min-width: 300px">
          <q-card-section>
            <div class="text-h6">Add tag</div>
          </q-card-section>
          <q-card-section>
            <q-input v-model="newLabel" label="Tag name" :error="!!labelError" :error-message="labelError" autofocus @keyup.enter="handleAddLabel" />
          </q-card-section>
          <q-card-actions align="right">
            <q-btn flat label="Cancel" v-close-popup @click="showAddLabelDialog = false" />
            <q-btn color="primary" text-color="grey-7" :loading="loadingLabel" label="Add" @click="handleAddLabel" />
          </q-card-actions>
        </q-card>
      </q-dialog>

      <q-dialog v-model="showRemoveLabelDialog" persistent>
        <q-card style="min-width: 300px">
          <q-card-section>
            <div class="text-h6">Remove tag</div>
          </q-card-section>
          <q-card-section>
            <p>Are you sure you want to remove the tag "{{ labelToRemove }}"?</p>
          </q-card-section>
          <q-card-actions align="right">
            <q-btn flat label="Cancel" v-close-popup @click="showRemoveLabelDialog = false" />
            <q-btn color="negative" :loading="loadingLabelRemove" label="Remove" @click="handleRemoveLabel" />
          </q-card-actions>
        </q-card>
      </q-dialog>
    </template>
  </q-dialog>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watchEffect } from '@vue/composition-api';

export default {
  name: 'GalleryLightbox',

  props: {
    content: {
      type: Array,
      required: true
    },
    startIndex: {
      type: Number,
      default: 0
    }
  },

  setup(props, { emit }) {
    const isOpen = ref(true);
    const currentIndex = ref(props.startIndex);
    const showAddLabelDialog = ref(false);
    const showRemoveLabelDialog = ref(false);
    const newLabel = ref('');
    const labelToRemove = ref('');
    const loadingLabel = ref(false);
    const loadingLabelRemove = ref(false);
    const labelError = ref('');
    const imageSizeMB = ref('Calculating...');
    const sidebarTab = ref('info');
    const commentInput = ref('');
    const addingComment = ref(false);
    const currentImage = computed(() => {
      return (
        props.content[currentIndex.value] || {
          url: '/placeholder.svg?height=800&width=1200',
          title: 'Forest Gump',
          aspect_ratio: 1.5,
          labels: [],
          comments: []
        }
      );
    });

    // Sort comments by created_at date (newest first)
    const sortedComments = computed(() => {
      if (!currentImage.value.comments || !Array.isArray(currentImage.value.comments)) {
        return [];
      }

      return [...currentImage.value.comments].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
    });
    async function handleAddLabel() {
      if (!newLabel.value.trim()) {
        labelError.value = 'Label cannot be empty';
        return;
      }
      loadingLabel.value = true;
      labelError.value = '';
      try {
        const { ContentApi } = await import('@/api/modules/content');
        const updatedLabels = await ContentApi.addLabelToImage({ id: currentImage.value.id, label: newLabel.value });
        currentImage.value.labels = updatedLabels;
        showAddLabelDialog.value = false;
        newLabel.value = '';
      } catch (e) {
        labelError.value = 'Failed to add label.';
      } finally {
        loadingLabel.value = false;
      }
    }

    function confirmRemoveLabel(label) {
      labelToRemove.value = label;
      showRemoveLabelDialog.value = true;
    }

    async function handleRemoveLabel() {
      if (!labelToRemove.value) return;

      loadingLabelRemove.value = true;
      try {
        const { ContentApi } = await import('@/api/modules/content');
        const updatedLabels = await ContentApi.removeLabelFromImage({ id: currentImage.value.id, label: labelToRemove.value });
        currentImage.value.labels = updatedLabels;
        showRemoveLabelDialog.value = false;
        labelToRemove.value = '';
      } catch (e) {
        console.error('Failed to remove label:', e);
      } finally {
        loadingLabelRemove.value = false;
      }
    }
    const handleKeyboardNavigation = e => {
      if (e.key === 'Escape') {
        emit('close');
      }
    };

    onMounted(() => {
      document.addEventListener('keydown', handleKeyboardNavigation);
      console.log(currentImage.value, 'current image opened'); // Log the current image inf
    });

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', handleKeyboardNavigation);
    });

    async function getFileSizeInMB(url) {
      if (!url || url.startsWith('/placeholder.svg')) {
        imageSizeMB.value = 'N/A';
        return;
      }
      imageSizeMB.value = 'Calculating...';
      try {
        // Directly use GET request as HEAD request is causing CORS issues
        const response = await fetch(url);
        let fileSize;
        if (response.ok) {
          const blob = await response.blob();
          fileSize = blob.size;
        } else {
          imageSizeMB.value = 'Error';
          console.error('Failed to fetch image size with GET:', response.status, response.statusText);
          return;
        }

        if (fileSize) {
          const sizeInBytes = parseInt(fileSize, 10);
          if (sizeInBytes < 1024) {
            imageSizeMB.value = sizeInBytes + ' B';
          } else if (sizeInBytes < 1024 * 1024) {
            imageSizeMB.value = (sizeInBytes / 1024).toFixed(2) + ' KB';
          } else {
            imageSizeMB.value = (sizeInBytes / (1024 * 1024)).toFixed(2) + ' MB';
          }
        } else {
          imageSizeMB.value = 'Unknown';
        }
      } catch (error) {
        console.error('Error fetching file size:', error);
        imageSizeMB.value = 'Error';
      }
    }

    watchEffect(() => {
      if (currentImage.value && currentImage.value.url) {
        getFileSizeInMB(currentImage.value.url);
      }
    });

    // Extract file extension from filename
    function getFileExtension(filename) {
      if (!filename) return 'Unknown';
      const parts = filename.split('.');
      if (parts.length > 1) {
        const extension = parts[parts.length - 1].toUpperCase();
        return extension;
      }
      return 'Unknown';
    }

    // Comments logic
    async function handleAddComment() {
      if (!commentInput.value.trim()) return;
      addingComment.value = true;
      try {
        const { ContentApi } = await import('@/api/modules/content');
        const newComment = await ContentApi.addCommentToImage({
          id: currentImage.value.id,
          content: commentInput.value.trim()
        });

        // Update the UI immediately with the new comment
        if (!Array.isArray(currentImage.value.comments)) {
          currentImage.value.comments = [];
        }
        currentImage.value.comments.unshift(newComment);

        // Clear the input field
        commentInput.value = '';
      } catch (error) {
        console.error('Error adding comment:', error);
      } finally {
        addingComment.value = false;
      }
    }

    // Format comment date to a user-friendly format
    function formatCommentDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      // Show relative time for recent comments
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
      if (diffHours < 24) {
        // Format like "Today, 12:30 PM"
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = hours % 12 || 12;
        const formattedMinutes = minutes.toString().padStart(2, '0');
        return `Today, ${formattedHours}:${formattedMinutes} ${ampm}`;
      }
      if (diffDays < 2) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;

      // For older comments, show the date
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }

    // Check if a comment has been edited
    function isCommentEdited(comment) {
      return comment.updated_at && comment.created_at !== comment.updated_at;
    }

    return {
      isOpen,
      currentIndex,
      currentImage,
      sortedComments,
      handleKeyboardNavigation,
      showAddLabelDialog,
      showRemoveLabelDialog,
      newLabel,
      labelToRemove,
      loadingLabel,
      loadingLabelRemove,
      labelError,
      handleAddLabel,
      confirmRemoveLabel,
      handleRemoveLabel,
      getFileExtension,
      imageSizeMB,
      sidebarTab,
      commentInput,
      addingComment,
      handleAddComment,
      formatCommentDate,
      isCommentEdited
    };
  }
};
</script>

<style scoped lang="scss">
.container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.top-bar {
  height: 64px;
  width: 100%;
  display: flex;
  align-items: center;
}

.filename {
  margin-right: auto;
  font-size: 24px;
}

.lightbox-container {
  display: flex;
  width: 100%;
  height: calc(100% - 64px);
  background-color: #f5f5f5;
  padding: 30px;
  gap: 12px;
}

.lightbox-content {
  position: relative;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  overflow: hidden;
}

.top-bar {
  /* Remove absolute positioning and gradient background */
  height: 64px;
  display: flex;
  align-items: center;
  padding: 35px 30px;
  background: #fff;
  color: #424242;
  z-index: 10;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.04);
  gap: 12px;
}

.lightbox-content {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  overflow: hidden;
}

.image-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
}

.sidebar {
  width: 320px;
  border-left: 1px solid #e0e0e0;
  background-color: white;
}

.sidebar-content {
  padding: 16px;
}

.sidebar-section {
  margin-bottom: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  color: #424242;
}

.section-title {
  margin-left: 8px;
  font-weight: 500;
  flex: 1;
}

.expand-icon {
  cursor: pointer;
}

.details-list {
  color: #616161;
}

.detail-item {
  display: flex;
  margin-bottom: 12px;
}

.detail-label {
  width: 100px;
  color: #757575;
}

.detail-value {
  font-weight: 500;
}

.tags-container,
.categories-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-chip,
.category-chip {
  background-color: #f5f5f5;
}

.add-tag-btn {
  margin-top: 8px;
  color: #1976d2;
}

.action-btn {
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  color: #212121;
  border-radius: 8px;
  min-height: 44px;
  height: 44px;
  padding: 0 18px;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.tabs {
  height: 100%;
  // width: 60px;
  background-color: #f5f5f5;
}

/* Comments section styles */
.comments-section {
  height: 100%;
  position: relative;
}

.comments-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  text-align: center;
  color: #757575;
}

.comments-list {
  padding: 8px 16px 100px 16px; /* Add bottom padding to ensure content isn't hidden behind the input section */
}

.comment-item {
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.comment-layout {
  display: flex;
  align-items: flex-start;
}

.comment-avatar {
  flex-shrink: 0;
  font-weight: 600;
  font-size: 20px;
  margin-right: 6px;
}

.comment-content-wrapper {
  flex: 1;
  min-width: 0; /* Ensures text wrapping works properly */
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.comment-author {
  font-weight: 600;
  font-size: 16px;
  color: #333333;
  margin-right: 12px;
}

.comment-time {
  font-size: 13px;
  color: #666666;
  white-space: nowrap;
}

.comment-edited {
  font-style: italic;
}

.comment-content {
  font-size: 15px;
  line-height: 1.5;
  color: #333333;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Comment input section styles */
.comment-input-section {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  border-top: 1px solid #f0f0f0;
  padding: 12px;
}

.comment-input-header {
  margin-bottom: 12px;
}

.comment-input-title {
  font-size: 16px;
  font-weight: 500;
  color: #424242;
}

.comment-input-container {
  display: flex;
  align-items: flex-start;
}

.comment-input-wrapper {
  display: flex;
  flex: 1;
  margin-left: 12px;
  align-items: center;
  gap: 8px;
}

.send-btn {
  position: absolute;
  background-color: #e6c6fc !important;
  font-size: 12px !important;
  padding: 3px;
  bottom: 24px;
  right: 24px;
}
</style>
